import { api } from './api';
import {
  ConversationDetail,
  ConversationListItem,
  ConversationListResponse,
} from 'Models/chat';

/**
 * Sidebar half of server-owned chat conversations (REV-201). The streaming turn
 * itself is not an RTK Query endpoint — it stays in `useStreamingChat`, which
 * reads the SSE body directly — so this slice only covers list, resume, delete.
 *
 * Tenant scoping is entirely server-side, off the JWT; nothing here takes a
 * tenant or company id. A conversation belonging to another tenant 404s.
 */
export const conversationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getConversations: build.query<ConversationListItem[], void>({
      query: () => ({ url: 'insights/chat/conversations', method: 'GET' }),
      // The backend wraps the rows in an envelope; the sidebar only wants the rows.
      transformResponse: (response: ConversationListResponse) => response.conversations,
      providesTags: [{ type: 'Conversations', id: 'LIST' }],
    }),
    getConversation: build.query<ConversationDetail, string>({
      query: (conversationId) => ({
        url: `insights/chat/conversations/${conversationId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, conversationId) => [
        { type: 'Conversations', id: conversationId },
      ],
    }),
    deleteConversation: build.mutation<void, string>({
      query: (conversationId) => ({
        url: `insights/chat/conversations/${conversationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, conversationId) => [
        { type: 'Conversations', id: conversationId },
        { type: 'Conversations', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetConversationQuery,
  useLazyGetConversationQuery,
  useDeleteConversationMutation,
} = conversationsApi;
